import { IBlock } from "../../../framework/src/IBlock";
import { Message } from "../../../framework/src/Message";
import { BlockComponent } from "../../../framework/src/BlockComponent";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { runEngine } from "../../../framework/src/RunEngine";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { getStorageData } from "framework/src/Utilities";

// Customizable Area Start
import _ from "lodash";
import React from "react";
import { initCometChat } from "../../../components/src/cometchatService";
import { CustomContext } from "../../../components/src/CustomContext.web";
import { scrollToTop } from "../../../components/src/utils";

interface IUser {
  id: string
  type: string
  attributes: {
    activated: true,
    country_code: string | null
    email: string
    first_name: string
    full_phone_number: string
    last_name: string
    height: string | null
    weight: string | null
    roles: string | null
    blocked: false,
    type: string | null
    status: string
    role_id: string | null
    gender: string | null
    full_name: string
    age: string | null
    date_of_birth: string | null
    locations: string | null
    languages: []
    description: string | null
    created_at: string
    updated_at: string
    unique_auth_id: string
    social_media_links: Object
    categories: []
    user_careers: []
    user_educations: []
    user_links: Object
    user_image_url: string
    cover_photo: string
    upload_media: []
  }
}

interface IUserListingSuccessResponse {
  accounts: {
    data: IUser[]
  }
  pagination: {
    current_page: number
    next_page: number | null
    total_count: number
    total_pages: number
  }
}

interface IUserListingErrorResponse {
  error: string
}

interface IOrderDetails {
  id: string
  type: string
  attributes: {
    id: number
    order_number: string
    status: string
    total: number
    gst: number
    sub_total: number
    valid_date: string
    active_plan: boolean
    order_date: string
    auto_renewal: boolean
    subscription: {
        id: number
        name: string
        price: string
    },
    account: {
        id: number
        full_name: string
        email: string
    }
  }
}

interface IActivePlanSuccessResponse {
  data: IOrderDetails
}

interface IActivePlanErrorResponse {
  error: string
}
// Customizable Area End

export const configJSON = require("./config");

// Customizable Area Start
// Cometchat interfaces start

export interface EntityWrapper {
  entity: EntityUser;
  entityType: string;
}

export interface CallOnEntity {
  sessionid: string;
  conversationId: string;
  sender: string;
  receiverType: string;
  receiver: string;
  status: string;
  type: string;
  duration: number
  data: {
    entities: {
      sender: EntityWrapper;
      receiver: EntityWrapper;
    };
  };
  initiatedAt: number;
  wsChannel: {
    identity: string;
    secret: string;
    service: string;
  };
  joinedAt: number;
}

export interface DataEntities {
  by: EntityWrapper;
  for: EntityWrapper;
  on: {
    entity: CallOnEntity;
    entityType: string;
  };
}

export interface CallData {
  action: string;
  entities: DataEntities;
  resource: string;
}

export interface CometCall {
  reactions: unknown[];
  mentionedUsers: unknown[];
  mentionedMe: boolean;
  receiverId: string;
  type: "audio" | "video";
  receiverType: "user" | "group";
  category: "call";
  action: "initiated" | "ended" | string;
  sessionId: string;
  status: "initiated" | "ongoing" | "ended" | "unanswered" | "busy";
  initiatedAt: number;
  joinedAt: number;
  id: string;
  conversationId: string;
  sender: User;
  receiver: User;
  readAt?: number
  data: CallData;
  sentAt: number;
  updatedAt: number;
  callInitiator: User;
  callReceiver: User;
  rawMessage: CometCall;
}

export interface IncomingMessageBase {
  id: string;
  conversationId: string;
  sender: User;
  readAt?: number
  deliveredAt?: number
  receiver: User;
  receiverId: string;
  receiverType: 'user' | 'group';
  category: 'message' | 'custom' | 'action';
  type: string;
  sentAt: number;
  updatedAt: number;
  text?: string;
  mentionedUsers: string[];
  mentionedMe: boolean;
  reactions: unknown[];
  rawMessage: Object;
}

export interface IncomingTextMessage extends IncomingMessageBase {
  category: 'message' | 'action';
  type: 'text';
  data: {
    text: string;
    resource: string;
    entities: BaseEntities;
  };
}

export interface IncomingMediaMessage extends IncomingMessageBase {
  category: 'message';
  type: 'image' | 'video' | 'audio' | 'file';
  data: {
    category: 'message';
    type: string;
    resource: string;
    url: string;
    attachments: AttachmentInfo[];
    entities: BaseEntities;
  };
}

export type IncomingMessage = IncomingTextMessage | IncomingMediaMessage | CometCall;

export interface User {
  hasBlockedMe: boolean;
  blockedByMe: boolean;
  deactivatedAt?: number;
  uid: string;
  name: string;
  lastActiveAt?: number;
  role: string;
  status: string;
  avatar?: string;
  conversationId: string;
}

export interface EntityUser {
  conversationId?: string
  lastActiveAt: number
  name: string
  role: string
  status: string
  uid: string
  avatar?: string
}

export interface BaseEntities {
  sender: {
    entity: EntityUser;
    entityType: 'user';
  };
  receiver: {
    entity: EntityUser;
    entityType: 'user';
  };
}

export interface AttachmentInfo {
  url: string;
  name: string;
  size: number;
  mimeType: string;
  extension: string
}

export interface CustomMessageData {
  customData: {
    text: string;
    attachments: {
      url: string;
      name: string;
      type: string;
    }[];
  };
  entities: BaseEntities;
  resource: string;
}

export interface ActionEntity {
  action: string;
  entities: {
    by: {
      entity: User;
      entityType: 'user';
    };
    for: {
      entity: User;
      entityType: 'user';
    };
    on: {
      entity: IncomingMediaMessage;
      entityType: 'message';
    };
  };
  resource?: string;
}

export interface Receipt {
  RECEIPT_TYPE: {
    READ_RECEIPT: string;
    DELIVERY_RECEIPT: string;
    READ_BY_ALL_RECEIPT: string;
    DELIVERED_TO_ALL_RECEIPT: string;
  };
  receiptType: string;
  sender: User;
  receiverType: string;
  receiver: string;
  readAt: number;
  messageId: string;
  timestamp: number;
}

export interface Conversation {
  conversationId: string;
  conversationType: string;
  lastReadMessageId: string;
  lastMessage: IncomingMessage;
  conversationWith: User;
  unreadMessageCount: number;
}
// Cometchat interfaces end

export interface IChat {
  id: string;
  muted?: boolean
  unreadCount?: number;
  lastMessage?: string;
  name: string;
  isOnline?: boolean
  lastMessageTime?: string
  profileImage?: string
}

interface IChatResponse {
  id: string;
  attributes: {
    name: string;
    accounts_chats: [
      {
        id: string;
        attributes: {
          account_id: number;
          muted: boolean;
          unread_count: number;
        };
      }
    ];
    messages: {
      id: string;
      attributes: { id: number; message: string };
      message: string;
    };
  };
}
// Customizable Area End
export interface Props {
  navigation: any;
  id: string;
  // Customizable Area Start
  // Customizable Area End
}

interface S {
  // Customizable Area Start
  token: string;
  isLoading: boolean
  accountId: number;
  chatName: string;
  chatList: IChat[];
  conversationsList: Conversation[]
  hasMoreConversations: boolean
  isChatsLoading: boolean
  searchValue: string
  searchedChatList: IUser[]
  isVisibleModal: boolean;
  searchListAnchorEl: HTMLDivElement | null
  isSearchingUser: boolean
  // Customizable Area End
}

interface SS {
  id: string;
  // Customizable Area Start
  // Customizable Area End
}

export default class ChatController extends BlockComponent<Props, S, SS> {
  // Customizable Area Start
  static contextType = CustomContext;
  getChatListApiCallId: string = "";
  createChatRoomApiCallId: string = "";
  getUsersListApiCallId: string = ""
  activePlanApiCallId: string = ""
  chatListRef: React.RefObject<HTMLDivElement> = React.createRef()
  conversationsRequest: CometChat.ConversationsRequest | null = null
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.receive = this.receive.bind(this);

    this.subScribedMessages = [
      // Customizable Area Start
      getName(MessageEnum.SessionResponseMessage),
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.NavigationPayLoadMessage),
      // Customizable Area End
    ];

    this.state = {
      // Customizable Area Start
      token: "",
      isLoading: true,
      accountId: -1,
      chatName: "",
      chatList: [],
      isChatsLoading: false,
      conversationsList: [],
      hasMoreConversations: true,
      searchValue: "",
      searchedChatList: [],
      isVisibleModal: false,
      searchListAnchorEl: null,
      isSearchingUser: false,
      // Customizable Area End
    };
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);

    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  get customContext(): React.ContextType<typeof CustomContext> {
    return this.context as React.ContextType<typeof CustomContext>;
  }
  
  async componentDidMount() {
    super.componentDidMount();
    this.getToken();
    // Customizable Area Start
    if (this.isPlatformWeb() === false) {
      this.props.navigation.addListener("willFocus", () => {
        this.getToken();
      });
    }
    this.getActivePlanApi()
    // Customizable Area End
  }

  async componentWillUnmount(): Promise<void> {
    this.removeCometChatListeners()
    this.customContext.setSelectedChatUID && this.customContext.setSelectedChatUID("")
    this.chatListRef.current?.removeEventListener('scroll', this.handleScroll)
  }

  handleScroll = () => {
    const element = this.chatListRef.current;
    if (!element || this.state.isChatsLoading) return;
  
    const nearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 100;
  
    if (nearBottom) {
      this.fetchConversations();
    }
  }

  handlePageLoad = () => {
    scrollToTop()
    initCometChat().then(() => {
      CometChat.getLoggedinUser().then(async user => {
        if(!user) {
          const cometAuthToken = await getStorageData(configJSON.COMET_AUTH_TOKEN_KEY)
          CometChat.login(cometAuthToken).then(
            (user) => {
              if(user) {
                this.initiatePageLoadFuncs()
              }
            },
            (error) => {
              console.log("Cometchat login failed:", { error });
            }
          );
        } else {
          this.initiatePageLoadFuncs()
        }
      }).catch(error => {
        console.log("Login error", error)
      })
    }).catch(error => {
      console.error("CometChat init error", error);
    })
  }

  updateConversationByMessage = (
    message: IncomingMessage,
    isSentByCurrentUser: boolean
  ) => {
    this.setState((prev) => {
      const conversations = [...prev.conversationsList];
      const isSender = !isSentByCurrentUser;
      const peerUID = isSender ? message.sender.uid : message.receiver.uid;
  
      const index = conversations.findIndex(
        (con) => con.conversationWith.uid === peerUID
      );
  
      const existingCon = index !== -1 ? conversations[index] : null;
      
      const receiverMessageCount =
        message.sender.uid === this.customContext.selectedChatUID
          ? 0
          : existingCon
          ? existingCon.unreadMessageCount + 1
          : 1;
      const updatedCon: Conversation = {
        conversationId: message.conversationId,
        conversationType: "user",
        lastReadMessageId: "",
        lastMessage: message,
        conversationWith: isSender ? message.sender : message.receiver,
        unreadMessageCount: isSender ? receiverMessageCount : 0,
      };
  
      if (index !== -1) {
        conversations.splice(index, 1);
      }
  
      conversations.unshift(updatedCon);
  
      return { conversationsList: conversations };
    });
  };  

  handleIncomingMessage = (message: IncomingMessage) => {
    this.updateConversationByMessage(message, false)
  }

  handleOutgoingMessage = (message: IncomingMessage) => {
    this.updateConversationByMessage(message, true)
  }

  initiatePageLoadFuncs = async () => {
    this.conversationsRequest = new CometChat.ConversationsRequestBuilder()
      .setLimit(30)
      .includeBlockedUsers(true)
      .build()
    this.addCometChatListeners()
    await this.fetchConversations()
    this.chatListRef.current?.addEventListener('scroll', this.handleScroll)
  }

  handleUserOnlineStatus = (user: User) => {
    this.setState(prev => ({
      conversationsList: prev.conversationsList.map(conversation => {
        if (conversation.conversationWith.uid === user.uid) {
          return {
            ...conversation,
            conversationWith: {
              ...conversation.conversationWith,
              status: user.status
            }
          };
        }
        return conversation;
      })
    }));
  }

  addCometChatListeners = () => {
    CometChat.addUserListener(
      configJSON.USER_PRESENCE_LISTENER,
      new CometChat.UserListener({
        onUserOnline: this.handleUserOnlineStatus,
        onUserOffline: this.handleUserOnlineStatus
      })
    );

    CometChat.addMessageListener(
      configJSON.NEW_MESSAGE_LISTENER_ID,
      new CometChat.MessageListener({
        onTextMessageReceived: this.handleIncomingMessage,
        onMediaMessageReceived: this.handleIncomingMessage,
        onCustomMessageReceived: this.handleIncomingMessage,
      })
    );
  }

  removeCometChatListeners = () => {
    CometChat.removeUserListener(configJSON.USER_PRESENCE_LISTENER)
    CometChat.removeMessageListener(configJSON.NEW_MESSAGE_LISTENER_ID)
  }

  getToken = () => {
    const message: Message = new Message(
      getName(MessageEnum.SessionRequestMessage)
    );
    this.send(message);
  };

  isStringNullOrBlank = (string: string) => {
    return string === undefined || string === null || string.length === 0;
  };

  showModal = () => {
    this.setState({ isVisibleModal: true });
  };

  hideModal = () => {
    this.setState({ isVisibleModal: false });
  };

  navigateToChatView = (chatId: string) => {
    const message = new Message(getName(MessageEnum.NavigationMessage));
    message.addData(getName(MessageEnum.NavigationTargetMessage), "ViewChat");

    message.addData(getName(MessageEnum.NavigationPropsMessage), this.props);

    const raiseMessage: Message = new Message(
      getName(MessageEnum.NavigationPayLoadMessage)
    );
    raiseMessage.addData(getName(MessageEnum.SessionResponseData), {
      chatId: chatId,
    });
    message.addData(getName(MessageEnum.NavigationRaiseMessage), raiseMessage);

    this.send(message);
  };

  getChatList = async (token: string) => {
    const header = {
      "Content-Type": configJSON.apiContentType,
      token,
    };
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.getChatListApiCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.getMyChatsApiEndpoint
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.getApiMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  createChatRoom = (chatName: string) => {
    if (this.isStringNullOrBlank(chatName)) {
      this.showAlert(
        configJSON.errorTitle,
        configJSON.errorAllFieldsAreMandatory,
        ""
      );
    } else {
      const header = {
        "Content-Type": configJSON.apiContentType,
        token: this.state.token,
        "Access-Control-Allow-Origin": "*",
      };
      const bodyData = {
        name: chatName,
      };
      const requestMessage = new Message(
        getName(MessageEnum.RestAPIRequestMessage)
      );

      this.createChatRoomApiCallId = requestMessage.messageId;

      requestMessage.addData(
        getName(MessageEnum.RestAPIResponceEndPointMessage),
        configJSON.createChatRoomApiEndPoint
      );
      requestMessage.addData(
        getName(MessageEnum.RestAPIRequestHeaderMessage),
        JSON.stringify(header)
      );
      requestMessage.addData(
        getName(MessageEnum.RestAPIRequestBodyMessage),
        JSON.stringify(bodyData)
      );
      requestMessage.addData(
        getName(MessageEnum.RestAPIRequestMethodMessage),
        configJSON.postApiMethod
      );

      runEngine.sendMessage(requestMessage.id, requestMessage);
    }
  };

  inputRoomNameProps = {
    onChangeText: (chatName: string) => {
      this.setState({ chatName });
    },
  };

  btnAddRoomProps = {
    onPress: () => this.createChatRoom(this.state.chatName),
  };

  btnCloseModalProps = {
    onPress: () => this.hideModal(),
  };

  btnShowAddModalProps = {
    onPress: () => {
      this.showModal();
    },
  };

  handleChatNameChange = (chatName: string) => {
    this.setState({ chatName });
  };

  async receive(from: string, message: Message) {
    if (getName(MessageEnum.NavigationPayLoadMessage) === message.id) {
      const data = message.getData(getName(MessageEnum.SessionResponseData));
      this.customContext.setSelectedChatUID(data.userUID)
    }

    const apiRequestCallId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );
    const responseJson = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );
    const errorResponse = message.getData(
      getName(MessageEnum.RestAPIResponceErrorMessage)
    );

    if (getName(MessageEnum.SessionResponseMessage) === message.id) {
      const token: string = message.getData(
        getName(MessageEnum.SessionResponseToken)
      );
      runEngine.debugLog("TOKEN", token);
    }
    if (
      responseJson?.data &&
      getName(MessageEnum.RestAPIResponceMessage) === message.id &&
      this.getChatListApiCallId.length > 0 &&
      apiRequestCallId === this.getChatListApiCallId
    ) {
      this.getChatListApiCallId = "";
      const chatList = responseJson.data;
      const results = chatList.map((item: IChatResponse) => {
        const findAccountMuteResult = item.attributes.accounts_chats.find(
          (item) => item.attributes.account_id === this.state.accountId
        )?.attributes.muted;
        return {
          id: item.id,
          name: item.attributes.name,
          muted:
            findAccountMuteResult ??
            item.attributes.accounts_chats[0].attributes.muted,
          unreadCount:
            item.attributes.accounts_chats[0].attributes.unread_count,
          lastMessage: item.attributes.messages?.attributes?.message,
        };
      });
      this.setState({
        chatList: results,
      });
    }
    if (
      getName(MessageEnum.RestAPIResponceMessage) === message.id &&
      this.createChatRoomApiCallId.length > 0 &&
      apiRequestCallId === this.createChatRoomApiCallId &&
      responseJson
    ) {
      this.createChatRoomApiCallId = "";
      this.hideModal();
      this.getChatList(this.state.token);
    }

    if(responseJson) {
      if(apiRequestCallId === this.getUsersListApiCallId) {
        this.handleUserListingApiResponse(responseJson)
      }

      if(apiRequestCallId === this.activePlanApiCallId) {
        this.handleActiveSubscriptionResponse(responseJson)
      }
    }
  }

  getActivePlanApi = async () => {
    this.setState({ isLoading: true })
    const token = await getStorageData(configJSON.TOKEN)

    const header = {
      "Content-Type": configJSON.validationApiContentType,
      token
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.activePlanApiCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.activePlanApiEndpoint
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.getApiMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  }

  handleActiveSubscriptionResponse = (
    responseJson: IActivePlanSuccessResponse | IActivePlanErrorResponse
  ) => {
    if ("data" in responseJson) {
      this.handlePageLoad()
      this.setState({ isLoading: false })
    } else {
      this.handlePageRedirection("SubscriptionPlans")
    }
  }

  handlePageRedirection = (path: string) => {
    const navigation = new Message(getName(MessageEnum.NavigationMessage));
    navigation.addData(getName(MessageEnum.NavigationTargetMessage), path);
    navigation.addData(getName(MessageEnum.NavigationPropsMessage), this.props);
    this.send(navigation);
  }

  debouncedFetchUsers = _.debounce((query: string) => {
    this.getUsersListApi(query);
  }, 500);

  handleSearchUsers = (value: string) => {
    const cleaned = value.trim();
    if (cleaned.length < 3) {
      this.setState({ searchedChatList: [] })
      return;
    }

    const query = `name=${cleaned}`;
    this.debouncedFetchUsers(query);
  };

  handleSearchValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    this.setState({ searchValue: value, isSearchingUser: true });
    this.handleSearchUsers(value);
  };

  handleSearchPopupClick = (event: React.MouseEvent<HTMLInputElement>) => {
    this.setState({ searchListAnchorEl: event.currentTarget })
  };

  handleSearchPopupClose = () => {
    this.setState({ searchListAnchorEl: null })
  };

  toggleChatSelection = (uid: string) => {
    const updatedChatUID = this.customContext.selectedChatUID === uid ? "" : uid
    this.customContext.setSelectedChatUID(updatedChatUID)
  }

  getUsersListApi = async (query: string) => {
    const endpoint = `${configJSON.usersListApiEndpoint}?${query}`
    const token = await getStorageData(configJSON.TOKEN)

    const header = {
      "Content-Type": configJSON.apiContentType,
      token,
    };
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.getUsersListApiCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      endpoint
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.getApiMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleUserListingApiResponse = (
    responseJson: IUserListingSuccessResponse | IUserListingErrorResponse
  ) => {
    if ("accounts" in responseJson) {
      const {
        accounts: { data }
      } = responseJson;
      this.setState({
        searchedChatList: data,
        isSearchingUser: false,
      });
    } else {
      this.setState({ searchedChatList: [], isSearchingUser: false });
    }
  }

  handleNewConversation = async (userUID: string) => {
    this.customContext.setSelectedChatUID(userUID)
    this.setState({
      searchListAnchorEl: null,
      searchValue: "",
      searchedChatList: []
    })
  }

  markReadMessagesCon = (conId: string) => {
    this.setState(prev => {
      const conversations = [...prev.conversationsList];
      const updatedCons = conversations.map((con) => {
        if(con.conversationId === conId) {
          return {
            ...con,
            unreadMessageCount: 0
          }
        }
        return con
      })

      return { conversationsList: updatedCons };
  });
  }

  fetchConversations = async () => {
    const { isChatsLoading, hasMoreConversations } = this.state
    if (isChatsLoading || !hasMoreConversations || !this.conversationsRequest) return
  
    this.setState({ isChatsLoading: true })
  
    try {
      const conversationList = await this.conversationsRequest.fetchNext()

      if (conversationList.length === 0) {
        this.setState({ isChatsLoading: false, hasMoreConversations: false })
        return
      }
      this.setState((prevState) => ({
        conversationsList: [
          ...prevState.conversationsList,
          ...(conversationList as Conversation[]),
        ],
        isChatsLoading: false,
      }))
    } catch (error) {
      console.error("Error fetching conversations:", error)
      this.setState({ isChatsLoading: false })
    }
  };

  handleRemoveConversation = (conId: string) => {
    this.setState((prevState) => ({
      conversationsList: [
        ...prevState.conversationsList.filter(
          (con) => con.conversationId !== conId
        ),
      ]
    }))
  }  
  // Customizable Area End
}
